import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { personasAPI, areasAPI } from '../services/api';

const getUltimaImputacionColor = (ultimaImputacion) => {
  if (!ultimaImputacion) {
    return 'black'; // Nunca ha imputado
  }
  
  const fechaImputacion = new Date(ultimaImputacion);
  const fechaActual = new Date();
  const diffTime = fechaActual - fechaImputacion;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  
  if (diffDays > 365) {
    return 'black'; // Más de un año sin imputar
  } else if (diffDays > 30) {
    return 'red'; // Más de un mes sin imputar
  } else {
    return 'black'; // Menos de un mes
  }
};

const formatUltimaImputacion = (ultimaImputacion) => {
  if (!ultimaImputacion) {
    return 'Nunca ha imputado';
  }
  
  const fechaImputacion = new Date(ultimaImputacion);
  const fechaActual = new Date();
  const diffTime = fechaActual - fechaImputacion;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Hoy';
  } else if (diffDays === 1) {
    return 'Ayer';
  } else if (diffDays < 7) {
    return `Hace ${diffDays} días`;
  } else if (diffDays < 30) {
    const semanas = Math.floor(diffDays / 7);
    return `Hace ${semanas} semana${semanas > 1 ? 's' : ''}`;
  } else if (diffDays < 365) {
    const meses = Math.floor(diffDays / 30);
    return `Hace ${meses} mes${meses > 1 ? 'es' : ''}`;
  } else {
    const años = Math.floor(diffDays / 365);
    return `Hace ${años} año${años > 1 ? 's' : ''}`;
  }
};

const PeopleList = () => {
  const [people, setPeople] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [filteredPeople, setFilteredPeople] = useState([]);

  useEffect(() => {
    loadPeople();
    loadAreas();
  }, []);

  useEffect(() => {
    loadPeople();
  }, [selectedArea]);

  useEffect(() => {
    // Filter people based on search term (only text search, area filtering is done by API)
    const filtered = people.filter(person =>
      person.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.apellido.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (person.area_nombre && person.area_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredPeople(filtered);
  }, [people, searchTerm]);

  const loadPeople = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedArea) {
        params.area = selectedArea;
      }
      const response = await personasAPI.getAll(params);
      const peopleData = response.data.results || response.data;
      setPeople(peopleData);
    } catch (err) {
      setError('Error al cargar las personas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAreas = async () => {
    try {
      const response = await areasAPI.getAll();
      setAreas(response.data.results || response.data);
    } catch (err) {
      console.error('Error al cargar las áreas:', err);
    }
  };

  if (loading) {
    return <div className="loading">Cargando personas...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Lista de Personas</h1>
        <div className="d-flex align-items-center">
          <select
            className="form-select"
            style={{ width: '200px', marginRight: '10px' }}
            value={selectedArea}
            onChange={(e) => setSelectedArea(e.target.value)}
          >
            <option value="">Todas las áreas</option>
            {areas.map(area => (
              <option key={area.id} value={area.id}>
                {area.nombre}
              </option>
            ))}
          </select>
          {selectedArea && (
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ marginRight: '10px' }}
              onClick={() => setSelectedArea('')}
              title="Limpiar filtro de área"
            >
              ✕
            </button>
          )}
          <input
            type="text"
            placeholder="Buscar personas..."
            className="form-control"
            style={{ width: '300px', marginRight: '10px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="text-muted">
            {filteredPeople.length} persona{filteredPeople.length !== 1 ? 's' : ''}
            {selectedArea && areas.find(a => a.id.toString() === selectedArea) && (
              <span className="text-info ms-1">
                (filtrado por: {areas.find(a => a.id.toString() === selectedArea).nombre})
              </span>
            )}
          </span>
        </div>
      </div>

      {filteredPeople.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm ? 'No se encontraron personas que coincidan con la búsqueda.' : 'No hay personas disponibles.'}
        </div>
      ) : (
        <div className="row">
          {filteredPeople.map(person => (
            <div key={person.id} className="col-lg-3 col-md-4 col-sm-6 mb-3">
              <div className="card h-100" style={{ fontSize: '0.9rem', padding: '0' }}>
                <div className="card-body p-3">
                  <h6 className="card-title mb-2">
                    <Link 
                      to={`/people/${person.id}`}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {person.full_name}
                    </Link>
                  </h6>
                  
                  <p className="card-subtitle text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                    {person.area_nombre || 'Sin área asignada'}
                  </p>
                  
                  {/* Initiatives Count and Names */}
                  <div className="mb-2">
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Iniciativas:</small>
                    <div style={{ fontSize: '0.85rem', fontWeight: '500', marginBottom: '4px' }}>
                      <span className={`badge ${person.iniciativas_count > 0 ? 'bg-primary' : 'bg-secondary'}`}>
                        {person.iniciativas_count || 0} iniciativa{person.iniciativas_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {person.iniciativas_nombres && person.iniciativas_nombres.length > 0 && (
                      <div style={{ fontSize: '0.75rem', color: '#666', lineHeight: 1.2 }}>
                        {person.iniciativas_nombres.map((nombre, index) => (
                          <div key={index} style={{
                            backgroundColor: '#f8f9fa',
                            padding: '2px 6px',
                            marginBottom: '2px',
                            borderRadius: '3px',
                            border: '1px solid #e9ecef',
                            fontSize: '0.7rem'
                          }}>
                            {nombre.length > 30 ? nombre.substring(0, 30) + '...' : nombre}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Última Imputación */}
                  <div className="mb-3">
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Última imputación:</small>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: getUltimaImputacionColor(person.ultima_imputacion),
                      fontWeight: '500'
                    }}>
                      {formatUltimaImputacion(person.ultima_imputacion)}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="text-center">
                    <Link to={`/people/${person.id}`} className="btn btn-outline-primary btn-sm">
                      Ver Perfil
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary by Area */}
      <div className="card mt-4">
        <div className="card-header">
          <h4 className="card-title">Resumen por Área</h4>
        </div>
        <div>
          {(() => {
            // Group people by area
            const byArea = filteredPeople.reduce((acc, person) => {
              const area = person.area_nombre || 'Sin área asignada';
              if (!acc[area]) acc[area] = [];
              acc[area].push(person);
              return acc;
            }, {});

            return Object.keys(byArea).length > 0 ? (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Área</th>
                      <th>Número de Personas</th>
                      <th>Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(byArea)
                      .sort(([,a], [,b]) => b.length - a.length)
                      .map(([area, peopleInArea]) => (
                        <tr key={area}>
                          <td>{area}</td>
                          <td>{peopleInArea.length}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div 
                                style={{
                                  width: '60px',
                                  height: '6px',
                                  backgroundColor: '#e9ecef',
                                  borderRadius: '3px',
                                  marginRight: '8px'
                                }}
                              >
                                <div
                                  style={{
                                    width: `${(peopleInArea.length / filteredPeople.length) * 100}%`,
                                    height: '100%',
                                    backgroundColor: '#dc3545',
                                    borderRadius: '3px'
                                  }}
                                />
                              </div>
                              {Math.round((peopleInArea.length / filteredPeople.length) * 100)}%
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted">No hay datos disponibles</p>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default PeopleList;